// #tag: NOTIFICATIONS INBOX SCREEN
// #purpose: Shows personalized student alert feed, category filters, mark-all-read triggers, and direct recruitment action buttons.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, X } from 'lucide-react';
import { PageHeaderSection } from '../components/cms/PageSections';
import { EmptyState, ErrorState, LoadingState } from '../components/states/StateViews';
import {
  useDismissNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationConfig,
  useNotifications,
  usePageSections,
} from '../hooks/useExamData';
import type { Notification } from '../types';

const PRIORITY_LABELS: Record<Notification['priority'], string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

function NotificationCard({
  notification,
  categoryLabel,
  onRead,
  onDismiss,
  onAction,
}: {
  notification: Notification;
  categoryLabel: string;
  onRead: (id: string) => void;
  onDismiss: (id: string) => void;
  onAction: (route: string) => void;
}) {
  return (
    <li className={`notification-item glass-surface ${notification.read ? '' : 'is-unread'}`}>
      <div className="wfx-notif-head">
        <span className={`chip-pill priority-${notification.priority}`}>{PRIORITY_LABELS[notification.priority]}</span>
        <span className="chip-pill type-category">{categoryLabel}</span>
        {!notification.read && <span className="wfx-unread-dot" aria-label="Unread" />}
      </div>
      <strong>{notification.title}</strong>
      {notification.examTitle && (
        <p className="wfx-notif-exam">
          {notification.examTitle}
          {notification.organization ? ` · ${notification.organization}` : ''}
        </p>
      )}
      <p>{notification.message}</p>
      <div className="wfx-notif-foot">
        <time className="wfx-muted">
          {new Date(notification.createdAt).toLocaleDateString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
        </time>
        <div className="wfx-notif-actions">
          {notification.actionRoute && (
            <button
              type="button"
              className="text-action"
              onClick={() => {
                onRead(notification.id);
                onAction(notification.actionRoute!);
              }}
            >
              {notification.actionLabel || 'View'}
            </button>
          )}
          {!notification.read && (
            <button type="button" className="text-action" onClick={() => onRead(notification.id)}>
              Mark read
            </button>
          )}
          <button
            type="button"
            className="ghost-icon-button"
            aria-label="Dismiss notification"
            onClick={() => onDismiss(notification.id)}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </li>
  );
}

export function NotificationsScreen() {
  const navigate = useNavigate();
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const { data: sectionsData } = usePageSections('notifications');
  const headerSection = sectionsData?.data?.[0];
  const { data: configData } = useNotificationConfig();
  const { data, isLoading, isError, refetch } = useNotifications({
    categoryId: categoryFilter ?? undefined,
  });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const dismiss = useDismissNotification();

  const categoryLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of configData?.categories ?? []) map.set(c.id, c.label);
    return map;
  }, [configData]);

  const notifications = data?.data ?? [];
  const unreadCount = data?.meta?.unread ?? notifications.filter((n) => !n.read).length;
  const followedExams = data?.meta?.followedExams ?? 0;

  if (isLoading) {
    return (
      <section className="adaptive-page">
        <LoadingState label="Loading notifications…" />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="adaptive-page">
        <ErrorState message="Could not load notifications." onRetry={() => refetch()} />
      </section>
    );
  }

  return (
    <section className="adaptive-page">
      {headerSection && <PageHeaderSection section={headerSection} />}

      <div className="wfx-notif-toolbar glass-surface">
        <div className="wfx-notif-toolbar-meta">
          <Bell size={18} aria-hidden="true" />
          <span>
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
            {followedExams > 0 ? ` · ${followedExams} followed exam${followedExams === 1 ? '' : 's'}` : ''}
          </span>
        </div>
        {unreadCount > 0 && (
          <button
            type="button"
            className="text-action wfx-notif-mark-all"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
          >
            <CheckCheck size={16} aria-hidden="true" />
            Mark all read
          </button>
        )}
      </div>

      {configData && (
        <div className="wfx-notif-filters" role="group" aria-label="Filter by category">
          <button
            type="button"
            className={`chip-pill ${categoryFilter === null ? 'is-active' : ''}`}
            onClick={() => setCategoryFilter(null)}
          >
            All
          </button>
          {configData.categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`chip-pill ${categoryFilter === cat.id ? 'is-active' : ''}`}
              onClick={() => setCategoryFilter(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {notifications.length === 0 ? (
        <EmptyState
          title={followedExams === 0 ? 'Follow exams to get updates' : 'No notifications in this category'}
          message={
            followedExams === 0
              ? 'Save exams from Results or Admit Cards — you will only receive updates for exams you follow.'
              : 'Try another category or check back when new updates are published.'
          }
          illustrationContext="empty.notifications"
        />
      ) : (
        <ul className="wfx-notifications">
          {notifications.map((n) => (
            <NotificationCard
              key={n.id}
              notification={n}
              categoryLabel={categoryLabels.get(n.categoryId) ?? n.categoryId.replace(/_/g, ' ')}
              onRead={(id) => markRead.mutate(id)}
              onDismiss={(id) => dismiss.mutate(id)}
              onAction={(route) => navigate(route)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
